import {
  Component,
  Inject,
  OnInit,
  ViewChild,
  Renderer2,
  ApplicationRef,
  NgZone,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DOCUMENT } from '@angular/common';
import { STEPPER_GLOBAL_OPTIONS, StepperSelectionEvent } from '@angular/cdk/stepper';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { filter, takeUntil, Subject } from 'rxjs';

import { DialogConfirmComponent } from '@app/shared/components/dialog-confirm/dialog-confirm.component';
import { MatStepper } from '@angular/material/stepper';
import { DriverService } from '@app/services/driver.service';
import { phoneValidator } from '@app/shared/helpers/validators.helper';
import { AccidentDialogStateService } from '@app/shared/services/accident-dialog-state.service';
import {
  DialogPositionService,
  DialogType,
} from '@app/shared/services/dialog-position.service';

@Component({
  selector: 'app-dialog-new-accident',
  templateUrl: './dialog-new-accident.component.html',
  styleUrls: ['./dialog-new-accident.component.scss'],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { showError: true },
    },
  ],
})
export class DialogNewAccidentComponent implements OnInit {
  accidentForm!: FormGroup;
  step: number = 0;
  isDoneStepEnabled!: boolean;
  insertedId: string = '';
  isMinimized: boolean = false;
  savedFormData: any = null;
  minimizedDialogElement: HTMLElement | null = null;
  backdropStyles: any = { opacity: '', pointerEvents: '' };
  isSaving: boolean = false;
  private dialogPosition: { right: string; bottom: string } = {
    right: '20px',
    bottom: '0px',
  };
  private destroy$ = new Subject<void>();
  private dialogBackdrop: HTMLElement | null = null;
  private mutationObserver: MutationObserver | null = null;

  @ViewChild('stepper') stepper!: MatStepper;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    @Inject(DOCUMENT) private document: Document,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<DialogNewAccidentComponent>,
    private driverService: DriverService,
    private renderer: Renderer2,
    private appRef: ApplicationRef,
    private ngZone: NgZone,
    private accidentDialogStateService: AccidentDialogStateService,
    private dialogPositionService: DialogPositionService
  ) {
    // Prevent dialog from closing when clicking outside
    this.dialogRef.disableClose = true;

    // Handle backdrop click with the same confirmation logic as the close button
    this.dialogRef.backdropClick().subscribe(() => {
      this.onDialogCloseClick();
    });

    // Add custom class to the dialog panel for z-index control
    this.dialogRef.addPanelClass('dialog-new-accident-overlay-pane');
  }

  ngOnInit(): void {
    this.setInitForm();

    // Observe changes in the DOM to detect selection panels and datepickers
    this.observeOverlayChanges();

    this.dialogPositionService.minimizedDialogs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isMinimized) {
          this.dialogPosition = this.dialogPositionService.getPositionForDialog(
            DialogType.ACCIDENT
          );
          this.updateMinimizedDialogPosition();
        }
      });

    this.dialogPositionService.screenResize$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isMinimized) {
          this.dialogPosition = this.dialogPositionService.getPositionForDialog(
            DialogType.ACCIDENT
          );
          this.updateMinimizedDialogPosition();
        }
      });
  }

  ngAfterViewInit(): void {
    this.stepper.selectedIndex = this.step;

    setTimeout(() => {
      const backdrops = this.document.querySelectorAll('.cdk-overlay-backdrop');
      if (backdrops.length > 0) {
        this.dialogBackdrop = backdrops[backdrops.length - 1] as HTMLElement;
      }

      // Set high z-index for the elements of the current overlay
      this.setHighZIndexForDialogElements();
    }, 0);
  }

  // Method to set high z-index for the elements of the dialog
  private setHighZIndexForDialogElements(): void {
    // Find the specific wrapper for this dialog using the custom class
    const dialogContainer = this.document.querySelector(
      '.dialog-new-accident-overlay-pane'
    );

    if (dialogContainer) {
      // Set z-index for the dialog panel
      this.renderer.setStyle(dialogContainer, 'z-index', '1001');

      // Find the parent overlay wrapper
      let parentElement = dialogContainer.parentElement;
      while (
        parentElement &&
        !parentElement.classList.contains('cdk-global-overlay-wrapper')
      ) {
        parentElement = parentElement.parentElement;
      }

      // If we find the wrapper, set its z-index
      if (parentElement) {
        this.renderer.setStyle(parentElement, 'z-index', '1001');

        // Find the corresponding backdrop (it will be the one with the same aria-modal attribute)
        const dialogId = dialogContainer.getAttribute('id');
        if (dialogId) {
          const backdrops = this.document.querySelectorAll('.cdk-overlay-backdrop');
          backdrops.forEach((backdrop) => {
            // Check if this backdrop is associated with our dialog
            if (this.isBackdropForDialog(backdrop as HTMLElement, dialogId)) {
              this.renderer.setStyle(backdrop, 'z-index', '1000');
            }
          });
        }
      }

      // Set z-index for the elements .cdk-overlay-connected-position-bounding-box
      // That may be related to this dialog (for dropdowns)
      this.updateConnectedOverlayBoundingBoxes();
    }
  }

  // Method to update the z-index of the bounding boxes for dropdowns
  private updateConnectedOverlayBoundingBoxes(): void {
    const boundingBoxes = this.document.querySelectorAll(
      '.cdk-overlay-connected-position-bounding-box'
    );

    boundingBoxes.forEach((box) => {
      // Check if this bounding box belongs to a dropdown related to our dialog
      if (this.isMinimized) {
        // If the dialog is minimized, restore the original z-index
        this.renderer.setStyle(box, 'z-index', '1000');
      } else {
        // If the dialog is active, set high z-index
        this.renderer.setStyle(box, 'z-index', '1001');
      }
    });
  }

  // Auxiliary method to determine if a backdrop belongs to a specific dialog
  private isBackdropForDialog(backdrop: HTMLElement, dialogId: string): boolean {
    // In Angular Material, backdrops usually have a class attribute that relates to the dialog
    // We can also use the position of the backdrop in the list of backdrops
    const backdropPosition = Array.from(
      this.document.querySelectorAll('.cdk-overlay-backdrop')
    ).indexOf(backdrop);
    const dialogPosition = Array.from(
      this.document.querySelectorAll('.cdk-overlay-pane')
    ).findIndex((pane) => pane.querySelector(`#${dialogId}`) !== null);

    // If they are in the same relative position, they probably belong to the same dialog
    return backdropPosition === dialogPosition;
  }

  setInitForm(): void {
    let driverId = '';
    if (this.data?.driver?.id) {
      driverId = this.data.driver.id;
    }

    let driverName = '';
    if (this.data?.driver?.driverName) {
      driverName = this.data.driver.driverName;
    }

    let organizationId = '';
    if (this.data?.driver?.organizationId) {
      organizationId = this.data.driver.organizationId;
    }
    this.accidentForm = this.fb.group({
      driverInfo: this.fb.group({
        organization: [organizationId, Validators.required],
        driverId: [{ value: '', disabled: true }, Validators.required],
        driverCode: [driverId],
        cellphone: ['', [Validators.required, phoneValidator()]],
        driverName: [{ value: driverName, disabled: true }],
        terminal: [{ value: null, disabled: true }],
        fleetManager: [{ value: null, disabled: true }],
        safetyManager: [{ value: null, disabled: true }],
        safetyTrainer: [{ value: null, disabled: true }],
      }),
      timePlace: this.fb.group({
        date: ['', Validators.required],
        time: [''],
        addressLine1: [''],
        addressLine2: [''],
        city: ['', Validators.required],
        state: ['', Validators.required],
        zip: [''],
        otherLocationDetails: [''],
      }),
      flaresHazmat: this.fb.group({
        accidentRoadway: [null, Validators.required],
        emergencyTriangles: [null],
        spilled: [null, Validators.required],
      }),
      injury: this.fb.group({
        injury: [null, Validators.required],
        injuryDescription: [null],
        medicalAssistance: [null],
      }),
      otherParty: this.fb.group({
        involved: [null, Validators.required],
        otherIndividualDescription: [null],
        fatalityInvolved: [null],
        otherIndividualInjured: [null],
        extentInjuriesDescription: [null],
        otherMedicalAssistance: [null],
        otherVehicleDamage: [null],
        extentDamagesDescription: [null],
        otherVehicleTowed: [null],
        towDescription: [null],
      }),
      police: this.fb.group({
        called: [null, Validators.required],
        respondingOfficerInfo: [null],
        reportNumber: [null],
        citation: [null],
      }),
      vehicleDamage: this.fb.group({
        truckNumber: [null, Validators.required],
        trailerNumber: [null],
        damage: [null, Validators.required],
        damageDescription: [null],
        cargo: [null],
        towed: [null, Validators.required],
        towInfoDescription: [null],
      }),
      accidentDetails: this.fb.group({
        assetsLocated: [null],
        crashType: [null],
        accidentDescription: [null],
        witnessInfoDescription: [null],
      }),
      done: this.fb.group({
        callHazmatResponse: [false],
        callAdminsInformFatality: [false],
        scheduleDriverTesting: [false],
      }),
    });
  }

  get driverInfoFormGroup(): FormGroup {
    return this.accidentForm.get('driverInfo') as FormGroup;
  }

  get timePlaceFormGroup(): FormGroup {
    return this.accidentForm.get('timePlace') as FormGroup;
  }

  get flaresHazmatFormGroup(): FormGroup {
    return this.accidentForm.get('flaresHazmat') as FormGroup;
  }

  get injuryFormGroup(): FormGroup {
    return this.accidentForm.get('injury') as FormGroup;
  }

  get otherPartyFormGroup(): FormGroup {
    return this.accidentForm.get('otherParty') as FormGroup;
  }

  get policeFormGroup(): FormGroup {
    return this.accidentForm.get('police') as FormGroup;
  }

  get vehicleDamageFormGroup(): FormGroup {
    return this.accidentForm.get('vehicleDamage') as FormGroup;
  }

  get accidentDetailsFormGroup(): FormGroup {
    return this.accidentForm.get('accidentDetails') as FormGroup;
  }

  get doneFormGroup(): FormGroup {
    return this.accidentForm.get('done') as FormGroup;
  }

  get driverNameValue(): string {
    return this.driverInfoFormGroup.get('driverName')?.value;
  }

  get dateValue(): string {
    return this.timePlaceFormGroup.get('date')?.value;
  }

  get showCallHazmat(): boolean {
    return this.flaresHazmatFormGroup.get('spilled')?.value === 'true';
  }
  get showCallGeneOrLaura(): boolean {
    return this.otherPartyFormGroup.get('fatalityInvolved')?.value === 'true';
  }
  get showScheduleAccidentTesting(): boolean {
    return (
      this.otherPartyFormGroup.get('fatalityInvolved')?.value === 'true' ||
      this.otherPartyFormGroup.get('otherMedicalAssistance')?.value === 'true' ||
      this.otherPartyFormGroup.get('otherVehicleTowed')?.value === 'true' ||
      this.accidentDetailsFormGroup.get('assetsLocated')?.value === 1
    );
  }

  onMinimizeClick(): void {
    this.savedFormData = this.accidentForm.getRawValue();
    this.isMinimized = true;
    this.dialogRef.addPanelClass('minimized');
    this.manipulateBackdrops(true);

    // Restore normal z-index in the bounding boxes when minimized
    this.updateConnectedOverlayBoundingBoxes();

    // Remove class from body when minimized
    this.renderer.removeClass(this.document.body, 'dialog-new-accident-open');

    this.accidentDialogStateService.setDialogMinimized(true);
    this.dialogPositionService.setDialogMinimized(DialogType.ACCIDENT, true);

    this.dialogPosition = this.dialogPositionService.getPositionForDialog(
      DialogType.ACCIDENT
    );

    this.ngZone.run(() => {
      this.createMinimizedDialogElement();
    });
  }

  onMaximizeClick(): void {
    if (this.minimizedDialogElement) {
      this.renderer.addClass(this.minimizedDialogElement, 'slide-down');

      setTimeout(() => {
        this.isMinimized = false;
        this.dialogRef.removePanelClass('minimized');
        this.manipulateBackdrops(false);

        if (this.savedFormData) {
          this.accidentForm.patchValue(this.savedFormData);
        }

        this.accidentDialogStateService.setDialogMinimized(false);
        this.dialogPositionService.setDialogMinimized(DialogType.ACCIDENT, false);

        this.removeMinimizedDialogElement();
        this.appRef.tick();

        // Restore high z-index for this dialog when maximizing it
        setTimeout(() => this.setHighZIndexForDialogElements(), 0);
      }, 300);
    } else {
      this.isMinimized = false;
      this.dialogRef.removePanelClass('minimized');
      this.manipulateBackdrops(false);

      if (this.savedFormData) {
        this.accidentForm.patchValue(this.savedFormData);
      }

      this.accidentDialogStateService.setDialogMinimized(false);
      this.dialogPositionService.setDialogMinimized(DialogType.ACCIDENT, false);
      this.appRef.tick();

      // Restore high z-index for this dialog when maximizing it
      setTimeout(() => this.setHighZIndexForDialogElements(), 0);
    }
  }

  manipulateBackdrops(minimize: boolean): void {
    if (!this.dialogBackdrop) return;

    if (minimize) {
      this.backdropStyles.opacity = this.dialogBackdrop.style.opacity || '0.5';
      this.backdropStyles.pointerEvents =
        this.dialogBackdrop.style.pointerEvents || 'auto';

      this.renderer.setStyle(this.dialogBackdrop, 'opacity', '0');
      this.renderer.setStyle(this.dialogBackdrop, 'pointer-events', 'none');
    } else {
      this.renderer.setStyle(this.dialogBackdrop, 'opacity', this.backdropStyles.opacity);
      this.renderer.setStyle(
        this.dialogBackdrop,
        'pointer-events',
        this.backdropStyles.pointerEvents
      );
    }
  }

  createMinimizedDialogElement(): void {
    this.minimizedDialogElement = this.renderer.createElement('div');
    this.renderer.addClass(this.minimizedDialogElement, 'minimized-dialog');

    this.renderer.setStyle(
      this.minimizedDialogElement,
      'right',
      this.dialogPosition.right
    );
    this.renderer.setStyle(
      this.minimizedDialogElement,
      'bottom',
      this.dialogPosition.bottom
    );

    this.renderer.listen(this.minimizedDialogElement, 'click', (event) => {
      this.ngZone.run(() => {
        this.onMaximizeClick();
      });
    });

    const content = this.renderer.createElement('div');
    this.renderer.addClass(content, 'minimized-content');

    const text = this.renderer.createElement('span');
    const driverId = this.driverInfoFormGroup.get('driverCode')?.value || '';

    if (driverId && driverId.trim() !== '') {
      text.textContent = `New Accident - ${driverId.toUpperCase()}`;
    } else {
      text.textContent = `New Accident`;
    }

    const maximizeIcon = this.renderer.createElement('i');
    this.renderer.addClass(maximizeIcon, 'fas');
    this.renderer.addClass(maximizeIcon, 'fa-up-right-and-down-left-from-center');
    this.renderer.addClass(maximizeIcon, 'maximize-icon');

    this.renderer.listen(maximizeIcon, 'click', (event) => {
      event.stopPropagation();
      this.ngZone.run(() => {
        this.onMaximizeClick();
      });
    });

    this.renderer.appendChild(content, text);
    this.renderer.appendChild(content, maximizeIcon);
    this.renderer.appendChild(this.minimizedDialogElement, content);

    this.renderer.appendChild(this.document.body, this.minimizedDialogElement);
  }

  updateMinimizedDialogPosition(): void {
    if (this.minimizedDialogElement) {
      this.renderer.setStyle(
        this.minimizedDialogElement,
        'right',
        this.dialogPosition.right
      );
      this.renderer.setStyle(
        this.minimizedDialogElement,
        'bottom',
        this.dialogPosition.bottom
      );
    }
  }

  removeMinimizedDialogElement(): void {
    if (this.minimizedDialogElement) {
      this.renderer.removeChild(this.document.body, this.minimizedDialogElement);
      this.minimizedDialogElement = null;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.removeMinimizedDialogElement();

    // Restore normal z-index in the bounding boxes when destroyed
    const boundingBoxes = this.document.querySelectorAll(
      '.cdk-overlay-connected-position-bounding-box'
    );
    boundingBoxes.forEach((box) => {
      this.renderer.setStyle(box, 'z-index', '1000');
    });

    // Disconnect the observer if it exists
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    if (this.isMinimized) {
      if (this.dialogBackdrop) {
        this.renderer.setStyle(
          this.dialogBackdrop,
          'opacity',
          this.backdropStyles.opacity
        );
        this.renderer.setStyle(
          this.dialogBackdrop,
          'pointer-events',
          this.backdropStyles.pointerEvents
        );
      }
    }

    this.accidentDialogStateService.setDialogMinimized(false);
    this.dialogPositionService.setDialogMinimized(DialogType.ACCIDENT, false);
  }

  onDialogCloseClick(): void {
    if (this.isMinimized) {
      this.onMaximizeClick();
      return;
    }

    if (this.accidentForm.dirty) {
      const data = {
        title: 'Unsaved changes',
        text: 'You have unsaved changes. Are you sure you want to leave this page? The changes you made will not be saved.',
      };
      const dialogRef = this.dialog.open(DialogConfirmComponent, {
        data,
        width: '500px',
        panelClass: 'high-z-unsaved-dialog',
      });

      setTimeout(() => {
        const unsavedDialogContainer = this.document.querySelector(
          '.high-z-unsaved-dialog'
        );
        if (unsavedDialogContainer) {
          this.renderer.setStyle(unsavedDialogContainer, 'z-index', '1002');

          let unsavedWrapper = unsavedDialogContainer.parentElement;
          while (
            unsavedWrapper &&
            !unsavedWrapper.classList.contains('cdk-global-overlay-wrapper')
          ) {
            unsavedWrapper = unsavedWrapper.parentElement;
          }

          if (unsavedWrapper) {
            this.renderer.setStyle(unsavedWrapper, 'z-index', '1002');
          }

          const backdrops = this.document.querySelectorAll('.cdk-overlay-backdrop');
          if (backdrops.length > 0) {
            const lastBackdrop = backdrops[backdrops.length - 1];
            this.renderer.setStyle(lastBackdrop, 'z-index', '1001');
          }
        }
      }, 0);

      dialogRef
        .afterClosed()
        .pipe(filter((closeModal: boolean) => closeModal))
        .subscribe(() => {
          this.accidentDialogStateService.setDialogMinimized(false);
          this.dialogRef.close();
        });
    } else {
      this.accidentDialogStateService.setDialogMinimized(false);
      this.dialogRef.close();
    }
  }

  onSaveClick(closeModal: boolean = false, editMode: boolean = false): void {
    // Prevent multiple calls if already saving
    if (this.isSaving) {
      return;
    }

    this.isSaving = true; // Disable buttons during save operation
    const request: any = this.createRequest();
    if (closeModal) {
      request.id = this.insertedId;
    }
    this.driverService.upsertAccident(request).subscribe({
      next: (res) => {
        this.isSaving = false; // Re-enable buttons on success
        this.insertedId = res[0].insertedId;
        this.showAccidentCreatedModal(editMode);
        this.isDoneStepEnabled = true;
        if (closeModal) {
          this.closeAccidentCreateModal();
        }
      },
      error: (error) => {
        this.isSaving = false; // Re-enable buttons on error
        console.error('Error saving accident', error);
      },
    });
  }

  onDoneClick(): void {
    if (
      this.showCallGeneOrLaura ||
      this.showCallHazmat ||
      this.showScheduleAccidentTesting
    ) {
      this.onSaveClick(true, true);
    } else {
      this.closeAccidentCreateModal();
    }
  }

  createRequest(): any {
    const request: any = {};
    const mapping: any = {
      driverInfo: {
        driverId: 'driverId',
        driverCode: 'driverCode',
        cellphone: 'driverPhone',
        organization: 'organization',
      },
      timePlace: {
        date: 'accidentDate',
        time: 'accidentTime',
        addressLine1: 'accidentAddress1',
        addressLine2: 'accidentAddress2',
        city: 'accidentCity',
        state: 'accidentState',
        zip: 'accidentZip',
        otherLocationDetails: 'accidentLocationOtherDetails',
      },
      flaresHazmat: {
        accidentRoadway: 'accidentOnRoadway',
        emergencyTriangles: 'deployedTriangleFlares',
        spilled: 'fuelHazardousSpilled',
      },
      injury: {
        injury: 'driverInjured',
        injuryDescription: 'driverInjuryDescription',
        medicalAssistance: 'driverRequireMedical',
      },
      otherParty: {
        involved: 'othersInvolved',
        otherIndividualDescription: 'othersInvolvedDetails',
        fatalityInvolved: 'fatalityInvolved',
        otherIndividualInjured: 'othersInjured',
        extentInjuriesDescription: 'othersInjuryDescription',
        otherMedicalAssistance: 'othersRequireMedical',
        otherVehicleDamage: 'othersVehiclePropertyDamage',
        extentDamagesDescription: 'othersVehiclePropertyDamageDescription',
        otherVehicleTowed: 'othersVehicleTowed',
        towDescription: 'othersVehicleTowedInformation',
      },
      police: {
        called: 'policeCalled',
        respondingOfficerInfo: 'policeOfficerInformation',
        reportNumber: 'policeReportNumber',
        citation: 'citationReceived',
      },
      vehicleDamage: {
        truckNumber: 'driverTruckNumber',
        trailerNumber: 'driverTrailerNumber',
        damage: 'driverEquipmentDamage',
        damageDescription: 'driverEquipmentDamageDescription',
        cargo: 'driverCargoCondition',
        towed: 'driverVehicleTowed',
        towInfoDescription: 'driverVehicleTowedInformation',
      },
      accidentDetails: {
        assetsLocated: 'assetsLocated',
        crashType: 'crashType',
        accidentDescription: 'accidentDescription',
        witnessInfoDescription: 'witnessInformation',
      },
      done: {
        callHazmatResponse: 'callHazmatResponse',
        callAdminsInformFatality: 'callAdminsInformFatality',
        scheduleDriverTesting: 'scheduleDriverTesting',
      },
    };

    for (const groupKey in this.accidentForm.controls) {
      const group = this.accidentForm.get(groupKey) as FormGroup;
      if (group && mapping[groupKey]) {
        for (const controlKey in group.controls) {
          const mappedKey = mapping[groupKey][controlKey];
          if (mappedKey) {
            let value = group.get(controlKey)?.value;
            if (value === 'true') {
              value = true;
            } else if (value === 'false') {
              value = false;
            }
            request[mappedKey] = value;
          }
        }
      }
    }
    request.accidentDate = this.convertToDatabaseFormat(
      request.accidentDate,
      request.accidentTime
    );
    request.othersInvolved = Number(request.othersInvolved);
    request.policeCalled = Number(request.policeCalled);
    if (!this.showCallHazmat) {
      request.callHazmatResponse = null;
    }
    if (!this.showCallGeneOrLaura) {
      request.callAdminsInformFatality = null;
    }
    if (!this.showScheduleAccidentTesting) {
      request.scheduleDriverTesting = null;
    }
    request.fatalityInvolved = request.fatalityInvolved === 'true' ? true : false;

    const storedData = localStorage.getItem('userData');
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      request.UserId = parsedData.userInfo.objectId;
    }

    request.organizationId = request?.organization?.organizationId;
    delete request.organization;

    request.driverId = request.driverCode;
    delete request.driverCode;

    return request;
  }

  convertToDatabaseFormat(accidentDate: string, accidentTime: string) {
    // Input validation: if no valid date, use current date
    if (
      !accidentDate ||
      typeof accidentDate !== 'string' ||
      !accidentDate.includes('/')
    ) {
      const now = new Date();
      const month = (now.getMonth() + 1).toString();
      const day = now.getDate().toString();
      const year = now.getFullYear().toString();
      accidentDate = `${month}/${day}/${year}`;
    }

    const [month, day, year] = accidentDate.split('/');
    const padZero = (value: any) => (value.length === 1 ? `0${value}` : value);

    // Time validation: if no valid time, use 00:00
    if (
      !accidentTime ||
      typeof accidentTime !== 'string' ||
      !accidentTime.includes(':')
    ) {
      return `${year}-${padZero(month)}-${padZero(day)} 00:00:00`;
    }

    try {
      const [hoursAndMinutes, period] = accidentTime.split(' ');
      let [hours, minutes] = hoursAndMinutes.split(':');

      if (period === 'PM' && hours !== '12') {
        hours = (parseInt(hours) + 12).toString();
      } else if (period === 'AM' && hours === '12') {
        hours = '00';
      } else {
        hours = padZero(hours.toString());
      }
      minutes = padZero(minutes);
      return `${year}-${padZero(month)}-${padZero(day)} ${hours}:${minutes}:00`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return `${year}-${padZero(month)}-${padZero(day)} 00:00:00`;
    }
  }

  showAccidentCreatedModal(edit: boolean = false): void {
    const driverName = this.driverInfoFormGroup.get('driverName')?.value;
    let modeTitle = 'Created';
    let modeText = 'created';
    if (edit) {
      modeTitle = 'Edited';
      modeText = 'edited';
    }
    const data = {
      title: `Accident ${modeTitle}`,
      text: `The accident for ${driverName} has been successfully ${modeText}. The details captured can be found on the driver\'s activity section.`,
      confirm: true,
    };
    const dialogRef = this.dialog.open(DialogConfirmComponent, {
      data,
      width: '500px',
      panelClass: 'high-z-created-dialog',
    });

    setTimeout(() => {
      const createdDialogContainer = this.document.querySelector(
        '.high-z-created-dialog'
      );
      if (createdDialogContainer) {
        this.renderer.setStyle(createdDialogContainer, 'z-index', '1002');

        let createdWrapper = createdDialogContainer.parentElement;
        while (
          createdWrapper &&
          !createdWrapper.classList.contains('cdk-global-overlay-wrapper')
        ) {
          createdWrapper = createdWrapper.parentElement;
        }

        if (createdWrapper) {
          this.renderer.setStyle(createdWrapper, 'z-index', '1002');
        }

        const backdrops = this.document.querySelectorAll('.cdk-overlay-backdrop');
        if (backdrops.length > 0) {
          const lastBackdrop = backdrops[backdrops.length - 1];
          this.renderer.setStyle(lastBackdrop, 'z-index', '1001');
        }
      }
    }, 0);

    dialogRef.afterClosed().subscribe(() => {
      this.nextStep();
    });
  }

  closeAccidentCreateModal(): void {
    this.dialogRef.close();
  }

  nextStep(): void {
    if (this.step < 9) {
      this.step++;
      this.stepper.next();
    }
  }

  prevStep(): void {
    if (this.step > 0) {
      this.step--;
      this.stepper.previous();
    }
  }

  reset(): void {
    this.step = 0;
    this.isDoneStepEnabled = false;
    this.stepper.reset();
  }

  onStepChange(event: StepperSelectionEvent): void {
    this.step = event.selectedIndex;
  }

  onSupportingMediaClick(): void {}

  // Method to observe changes in the overlay container and adjust z-index
  private observeOverlayChanges(): void {
    // Use MutationObserver to detect when new overlays are added (like selectors or datepickers)
    const overlayContainer = this.document.querySelector('.cdk-overlay-container');
    if (overlayContainer) {
      this.mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            // If a new node has been added, check if it is related to our dialog
            setTimeout(() => {
              // Identify the container of the current dialog
              const accidentDialogContainer = this.document.querySelector(
                '.dialog-new-accident-overlay-pane'
              );
              if (!accidentDialogContainer) return;

              // Find the parent wrapper
              let dialogWrapper = accidentDialogContainer.parentElement;
              while (
                dialogWrapper &&
                !dialogWrapper.classList.contains('cdk-global-overlay-wrapper')
              ) {
                dialogWrapper = dialogWrapper.parentElement;
              }
              if (!dialogWrapper) return;

              // Only modify the z-index if the dialog is active (not minimized)
              if (!this.isMinimized) {
                // Set z-index of the wrapper
                this.renderer.setStyle(dialogWrapper, 'z-index', '1001');

                // Find selectors, autocomplete and datepickers inside or related to our dialog
                const selectPanels = this.document.querySelectorAll(
                  '.mat-mdc-select-panel'
                );
                const autocompletePanels = this.document.querySelectorAll(
                  '.mat-mdc-autocomplete-panel'
                );
                const datepickerContent = this.document.querySelectorAll(
                  '.mat-datepicker-content'
                );

                // Find the elements .cdk-overlay-connected-position-bounding-box
                const boundingBoxes = this.document.querySelectorAll(
                  '.cdk-overlay-connected-position-bounding-box'
                );

                // Only set high z-index if they are related to our dialog
                // We can determine this by checking if they are children of the wrapper or if they were the last ones added
                selectPanels.forEach((panel) => {
                  if (
                    this.isElementRelatedToDialog(
                      panel as HTMLElement,
                      dialogWrapper as HTMLElement
                    )
                  ) {
                    this.renderer.setStyle(panel as HTMLElement, 'z-index', '1002');
                  }
                });

                autocompletePanels.forEach((panel) => {
                  if (
                    this.isElementRelatedToDialog(
                      panel as HTMLElement,
                      dialogWrapper as HTMLElement
                    )
                  ) {
                    this.renderer.setStyle(panel as HTMLElement, 'z-index', '1002');
                  }
                });

                datepickerContent.forEach((content) => {
                  if (
                    this.isElementRelatedToDialog(
                      content as HTMLElement,
                      dialogWrapper as HTMLElement
                    )
                  ) {
                    this.renderer.setStyle(content as HTMLElement, 'z-index', '1002');
                  }
                });

                // Set z-index for the bounding boxes
                boundingBoxes.forEach((box) => {
                  this.renderer.setStyle(box, 'z-index', '1001');
                });
              }
            }, 0);
          }
        });
      });

      this.mutationObserver.observe(overlayContainer, { childList: true, subtree: true });
    }
  }

  // Auxiliary method to determine if an element is related to our dialog
  private isElementRelatedToDialog(
    element: HTMLElement,
    dialogWrapper: HTMLElement
  ): boolean {
    // If the element is inside the dialog wrapper
    if (dialogWrapper.contains(element)) {
      return true;
    }

    // If the element is one of the most recently added to the DOM and is inside an overlay pane
    // This is useful for datepickers and selectors that are added as sibling nodes
    const isRecentlyAdded =
      Array.from(this.document.querySelectorAll('.cdk-overlay-pane')).indexOf(
        element.closest('.cdk-overlay-pane') as HTMLElement
      ) >= 0;

    // If the user is interacting with our dialog (it is the active one),
    // we assume that the recently added UI elements are related to it
    return isRecentlyAdded && !this.isMinimized;
  }
}
